// src/components/ProfileForm.jsx

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { isAtLeastAge } from "../lib/ageUtils";
import Modal from "./Modal";
import SidebarNavbar from "./SidebarNavbar";
import BottomNavbar from "./BottomNavbar";
import { Button as FcButton, Card as FcCard, Input as FcInput } from '../ui';

// Simple device detection for database storage
function getSimpleDeviceType() {
  if (typeof window === 'undefined') return 'unknown';
  const userAgent = window.navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  if (/android/.test(userAgent)) return 'android';
  return 'desktop';
}

export default function ProfileForm({ inline = false, onComplete }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [selfGender, setSelfGender] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [error, setError] = useState("");

  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("profiles")
        .select("username, date_of_birth, gender, mobile_number")
        .eq("id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
      }

      if ((!data || !data.username) && !inline) {
        setShowWelcome(true);
      } else if (inline && data?.username && onComplete) {
        onComplete();
        return;
      }

      setProfile(data);
      setUsername(data?.username || "");
      setDob(data?.date_of_birth || "");
      setGender(data?.gender || "");
      setMobileNumber(data?.mobile_number || "");
      setLoading(false);
    };

    fetchProfile();
  }, [inline, onComplete]);

  const handleSave = async () => {
    setError("");
    if (!dob) {
      setError("Please enter your date of birth.");
      return;
    }

    // Basic mobile number validation if provided
    if (mobileNumber && !/^\+\d{10,15}$/.test(mobileNumber)) {
      setError("Please enter a valid mobile number with country code (e.g., +1234567890).");
      return;
    }

    if (!isAtLeastAge(dob, 13)) {
      setError("You must be at least 13 years old to join Footy Coach.");
      return;
    }

    setUpdating(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const finalGender = gender === "self-describe" ? selfGender : gender;
    const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const { error } = await supabase.from("profiles").upsert({
      id: user.id,
      email: user.email,
      username,
      date_of_birth: dob,
      gender: finalGender,
      time_zone: detectedTimeZone,
      mobile_number: mobileNumber || null,
      device_type: getSimpleDeviceType(),
    });

    setUpdating(false);

    if (error) {
      setError("Failed to update profile. Please try again.");
    } else {
      if (!inline) {
        setCompleted(true);
      } else if (onComplete) {
        onComplete();
      }
    }
  };

  if (loading) {
    const loadingMessage = <p className="p-6 text-brand-text">Loading profile…</p>;
    return inline ? <Modal title="Profile Form" onClose={() => {}}>{loadingMessage}</Modal> : (
      <>
        {/* Green ribbon header for mobile */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-ui-green py-4 px-6 flex items-center">
          <span className="text-white text-xl font-semibold">Footy Coach</span>
        </div>
        {/* Offset for fixed banner */}
        <div className="pt-14">
          <SidebarNavbar handleLogout={handleLogout} userRole={null} />
          <BottomNavbar handleLogout={handleLogout} userRole={null} />
          <div role="main" id="fc-main-content" className="md:ml-64 px-2 sm:px-4 md:p-8 pb-28 fc-workspace">
            {loadingMessage}
          </div>
        </div>
      </>
    );
  }

  if (completed && !inline) {
    const completedUI = (
      <FcCard as="div" padding="standard" className="p-6 bg-surface-card rounded shadow-md w-96 mx-auto mt-10 text-center">
        <h2 className="text-2xl font-bold mb-4 text-brand-text">Profile Complete!</h2>
        <p className="mb-6 text-brand-text-muted">
          Your profile has been saved successfully. You're all set to start using Footy Coach.
        </p>
        <FcButton variant="primary" type="submit"
          onClick={() => {
            const pendingJoin = localStorage.getItem('pendingJoin');
            navigate("/");
          }}
          className="w-full"
        >
          Continue to Dashboard
        </FcButton>
      </FcCard>
    );
    return (
      <>
        {/* Green ribbon header for mobile */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-ui-green py-4 px-6 flex items-center">
          <span className="text-white text-xl font-semibold">Footy Coach</span>
        </div>
        {/* Offset for fixed banner */}
        <div className="pt-14">
          <SidebarNavbar handleLogout={handleLogout} userRole={null} />
          <BottomNavbar handleLogout={handleLogout} userRole={null} />
          <div role="main" id="fc-main-content" className="md:ml-64 px-2 sm:px-4 md:p-8 pb-28 fc-workspace">
            <div className="mt-10">
              {completedUI}
            </div>
          </div>
        </div>
      </>
    );
  }

  const form = (
    <FcCard as="div" padding="standard"
      className={`p-6 ${inline ? "" : "bg-surface-card rounded shadow-md max-w-md mx-auto"}`}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-ui-error rounded">{error}</div>
      )}
      
      {/* Welcome banner */}
      {showWelcome && !inline && (
        <div className="mb-8">
          <div className="p-6 bg-gradient-to-r from-[#284239] to-[#3a5a4d] rounded-lg shadow-md mb-6">
            <h2 className="text-white text-2xl font-bold mb-2">🎉 Welcome to Footy Coach!</h2>
            <p className="text-white/90">
              You're almost ready! Just a few quick details to personalise your experience.
            </p>
          </div>
          
          {/* Why this matters section */}
          <div className="bg-brand-surface border border-brand-border rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-brand-text mb-4">Why this matters</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="text-ui-success mt-0.5">✓</div>
                <div>
                  <span className="font-medium text-brand-text">Age verification</span>
                  <p className="text-sm text-brand-text-muted">Ensures you're 13+ to join, with 18+ checks for adult-only competitions</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="text-ui-success mt-0.5">✓</div>
                <div>
                  <span className="font-medium text-brand-text">Personalisation</span>
                  <p className="text-sm text-brand-text-muted">Helps us tailor your experience and recommendations</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="text-ui-success mt-0.5">✓</div>
                <div>
                  <span className="font-medium text-brand-text">Notifications</span>
                  <p className="text-sm text-brand-text-muted">Optional SMS alerts for important updates and reminders</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!inline && !showWelcome && <h2 className="text-2xl font-bold mb-4 text-brand-text">Your Profile</h2>}

      <label className="block mb-2 font-semibold text-brand-text">Username</label>
      <FcInput aria-label="Username"
        type="text"
        className="w-full mb-4 border border-transparent"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />

      <label className="block mb-2 font-semibold text-brand-text">Date of Birth</label>
      <FcInput aria-label="Username"
        type="date"
        className="w-full mb-4 border border-transparent"
        value={dob}
        onChange={(e) => setDob(e.target.value)}
        required
      />

      <fieldset className="mb-4">
        <legend className="block mb-2 font-semibold text-brand-text">Gender</legend>
        <div className="space-y-2">
          {[
            { value: "male", label: "Male" },
            { value: "female", label: "Female" },
            { value: "nonbinary", label: "Non-binary" },
            { value: "self-describe", label: "Prefer to self-describe" },
            { value: "prefer-not-to-say", label: "Prefer not to say" },
          ].map(({ value, label }) => (
            <label key={value} className="flex items-center space-x-2 text-brand-text cursor-pointer">
              <FcInput aria-label="gender"
                type="radio"
                name="gender"
                value={value}
                checked={gender === value}
                onChange={() => setGender(value)}
                className="w-4"
                required
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {gender === "self-describe" && (
          <div className="mt-2">
            <FcInput aria-label="Your term here"
              type="text"
              className="w-full mb-1 border border-transparent"
              placeholder="Your term here"
              value={selfGender}
              onChange={(e) => setSelfGender(e.target.value)}
              required
              maxLength={50}
            />
            <p className="text-sm text-brand-text-muted">
              {selfGender.length} / 50 characters
            </p>
          </div>
        )}
      </fieldset>

      <label className="block mb-2 font-semibold text-brand-text">
        Mobile Number 
        <span className="text-sm font-normal text-brand-text-muted ml-1">(optional - for SMS notifications)</span>
      </label>
      <FcInput aria-label="+1234567890 (include country code)"
        type="tel"
        className="w-full mb-6 border border-transparent"
        placeholder="+1234567890 (include country code)"
        value={mobileNumber}
        onChange={(e) => setMobileNumber(e.target.value)}
      />

      {/* What happens next section */}
      {showWelcome && (
        <div className="bg-brand-surface border border-brand-border rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-brand-text mb-4">What happens next?</h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-ui-green text-white flex items-center justify-center text-sm font-semibold">1</div>
              <div>
                <p className="text-brand-text font-medium">Complete your profile</p>
                <p className="text-sm text-brand-text-muted">Fill in the details above</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-ui-green text-white flex items-center justify-center text-sm font-semibold">2</div>
              <div>
                <p className="text-brand-text font-medium">Browse competitions</p>
                <p className="text-sm text-brand-text-muted">Find the perfect league to join</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-ui-green text-white flex items-center justify-center text-sm font-semibold">3</div>
              <div>
                <p className="text-brand-text font-medium">Start playing</p>
                <p className="text-sm text-brand-text-muted">Select your teams and compete!</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <FcButton variant="primary" type="submit"
        onClick={handleSave}
        className="w-full"
        disabled={updating}
      >
        {updating ? "Saving…" : inline ? "Continue" : showWelcome ? "Save & Start Playing" : "Save Profile"}
      </FcButton>
    </FcCard>
  );

  return inline ? <Modal title="Profile Form" onClose={() => {}}>{form}</Modal> : (
    <>
      {/* Green ribbon header for mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-ui-green py-4 px-6 flex items-center">
        <span className="text-white text-xl font-semibold">Footy Coach</span>
      </div>
      {/* Offset for fixed banner */}
      <div className="pt-14">
        <SidebarNavbar handleLogout={handleLogout} userRole={null} />
        <BottomNavbar handleLogout={handleLogout} userRole={null} />
        <div role="main" id="fc-main-content" className="md:ml-64 px-2 sm:px-4 md:p-8 pb-28 fc-workspace">
          <div className="mt-10">
            {form}
          </div>
        </div>
      </div>
    </>
  );
}
